/**
 * Asset constituent model
 * @class
 */
import Base from '@netx/core/lib/netx-model';

import AssetSetModel from './assetSet';
import {types as assetSetTypes} from '../properties/assetSets';

export default class AssetConstituent extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * The unique ID for this model is "id"
	 * @type {string}
	 */
	get idAttribute() {
		return 'constituentId';
	}

	/**
	 * @property {object}  defaults                - The default attribute values
	 * @property {number}  defaults.constituentId  - Constituent id (Never set the id in defaults)
	 * @property {number}  defaults.display        - Display constituent (boolean as number)
	 * @property {number}  defaults.assetId        - Asset id (the asset I am a constituent of)
	 * @property {string}  defaults.lowresUrl      - Low resolution image (unused?)
	 * @property {string}  defaults.modDate        - Modification date string
	 * @property {string}  defaults.previewUrl     - Preview image
	 * @property {number}  defaults.rebuildCount   - ??? Rebuild count
	 * @property {number}  defaults.rebuildType    - ??? Rebuild type
	 * @property {number}  defaults.ref            - ???
	 * @property {string}  defaults.thumbUrl       - Thumbnail image
	 * @property {string}  defaults.title          - Title
	 * @property {number}  defaults.type           - 1 = time (keyframe), 2 = layer, 3 = page, 4 = clips
	 */
	get defaults() {
		return {
			// constituentId : 0,
			attributes: [],
			display: 0,
			assetId: 0,
			lowresUrl: '',
			modDate: '',
			previewUrl: '',
			rebuildType: 0,
			rebuildCount: 0,
			ref: 1,
			thumbUrl: '',
			title: '',
			type: 0,
		};
	}

	/**
	 * @property {object}         methods              - Model RPC methods
	 * @property {function|array} methods.read         - Fetch constituent
	 * @property {function|array} methods.setDisplay   - Tell the dam to use this constituent as the image thumbnail/preview
	 */
	get methods() {
		return {
			read: ['getConstituentObject', 'constituentId'],
			setDisplay: ['setConstituentDisplay', 'constituentId'],
		};
	}

	/**
	 * @property {object}   parsers             - Model parsers
	 * @property {function} parsers.setDisplay  - Parse the return of our fetch, it is an array with  a single record
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {null} result Result (returns `null` - very helpful)
			 * @param {object} opts Request options
			 */
			setDisplay(result, opts) {
				this.set({display: 1});
				return result;
			},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		/**
		 * @memberof module:asset/models/assetConstituent
		 * @alias clipAttributes
		 * @type {module:asset/collections/attributes}
		 */
		this.clipAttributes = opts.clipAttributes;
	}

	/**
	 * Check our permissionMatrix for whether a particular named bit is set.
	 * Pipes through the asset we belong to
	 * @method
	 * @param {string} perm One of the permissions in {module:permissions/properties/assetPermissions#matrixNames}
	 * @returns {mixed} returns true or false, or undefined (which is falsy) if the argument is undefined.
	 */
	allows(perm) {
		// TODO: better way to handle the contituents having access to the asset bean they belong to
		// Maybe, rather than passing an assetId to constituents we passa full on bean - that way when it updates
		// we update and have proper permission matrix rather than relying that the constituent's asset will always
		// be Netx.currentAsset
		return Netx.currentAsset.allows.apply(Netx.currentAsset, arguments);
	}

	/**
	 * Return an assetSet object defining the set that contains just this constituent.
	 * @method
	 * @return {module:asset/models/assetSet} AssetSet model
	 */
	toAssetSet() {
		return new AssetSetModel({
			type: assetSetTypes.asset,
			ids: [this.get('assetId')],
			models: [this],
			label: this.get('title'),
			canCreateLink: false,
			canEmail: false,
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'AssetConstituent';
	}
}
